import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Headphones } from "lucide-react";
import { SectionHeading } from "../components/PageBits";
import { Reveal, RevealLines } from "../components/Reveal";

const PLATFORMS = ["Spotify", "Apple Podcasts", "YouTube"];

export default function Podcast() {
  return (
    <>
      {/* 1. Hero */}
      <section className="grain relative overflow-hidden bg-navy pt-40 pb-16 sm:pt-48 sm:pb-24" data-testid="podcast-hero">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[150px]" />
        </div>
        <div className="container-luxe relative z-10">
          <Reveal>
            <p className="overline mb-7 flex items-center gap-4">
              <span className="h-px w-10 bg-gold" /> Podcast
            </p>
          </Reveal>
          <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.04] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            <RevealLines lines={["Las ideas también"]} delay={0.2} />
            <RevealLines lines={["necesitan conversación."]} lineClassName="italic text-gold" delay={0.32} />
          </h1>
          <div className="mt-8 max-w-2xl space-y-5">
            <Reveal delay={0.5}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                Algunas ideas se comprenden estudiándolas. Otras necesitan ponerse en diálogo para
                descubrir nuevos matices.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                El Podcast de Desde el Primer Latido™ es un espacio para conversar, reflexionar y seguir
                ampliando la comprensión del ser humano desde distintas perspectivas.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. ¿Qué encontrarás aquí? */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="podcast-what">
        <div className="container-luxe">
          <SectionHeading
            dark={false}
            overline="Conversaciones que amplían la mirada"
            title="Más que respuestas,"
            accent="nuevas preguntas."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-7">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  Aquí las ideas continúan evolucionando.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  Cada conversación abre nuevas perspectivas, comparte experiencias y permite explorar
                  temas relacionados con la conciencia, las emociones, el crecimiento personal y el
                  desarrollo humano desde un formato cercano y natural.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-navy">
                No es una clase.
                <br />
                <span className="italic text-gold-deep">Es una conversación.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. Voces que enriquecen el camino */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="podcast-voices">
        <div className="container-luxe relative z-10">
          <SectionHeading
            overline="Distintas perspectivas"
            title="Conversaciones que enriquecen"
            accent="el recorrido."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-7">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-cream/75">
                  El Podcast abre un espacio para conversaciones, reflexiones y encuentros con distintas
                  perspectivas que enriquecen la comprensión del ser humano.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg font-light leading-relaxed text-cream/75">
                  Algunos episodios serán conversaciones con invitados. Otros serán reflexiones
                  personales.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-cream">
                Todos compartirán una misma intención:
                <br />
                <span className="italic text-gold">seguir aprendiendo juntos.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 4. Cómo escucharlo */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="podcast-listen">
        <div className="container-luxe">
          <SectionHeading dark={false} overline="Disponible para ti" title="Escúchalo donde y" accent="cuando quieras." />
          <div className="max-w-2xl space-y-6">
            <Reveal>
              <p className="text-lg font-light leading-relaxed text-navy/80">
                Acompaña tu día con conversaciones que invitan a reflexionar sin necesidad de detener tu
                rutina.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-navy/80">
                Disponible en las plataformas oficiales de la Academia.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-3 pt-2">
                {PLATFORMS.map((p) => (
                  <span
                    key={p}
                    className="flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-sm font-light text-navy/70"
                    data-testid={`podcast-platform-${p}`}
                  >
                    <Headphones size={15} className="text-gold-deep" /> {p}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <p className="text-xs font-light uppercase tracking-luxe text-navy/40" data-testid="podcast-platform-note">
                Próximamente en plataformas seleccionadas
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5. Cierre */}
      <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="podcast-closing">
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-snug text-cream sm:text-3xl">
              Si deseas comprender el método con mayor profundidad, visita la Academia.
              <br />
              Si deseas profundizar las ideas por escrito, descubre la colección de Libros.
              <br />
              <span className="italic text-gold">El Podcast es un espacio para seguir conversando.</span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Link
                to="/academy"
                className="group inline-flex items-center gap-2 text-base text-gold underline-offset-4 transition-colors hover:underline"
                data-testid="podcast-link-academy"
              >
                Conocer la Academia
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link
                to="/books"
                className="group inline-flex items-center gap-2 text-base text-gold underline-offset-4 transition-colors hover:underline"
                data-testid="podcast-link-books"
              >
                Explorar los Libros
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
